/** @module csi/locale */
define([
	"dojo/_base/lang",
	"dojo/_base/window",
	"csi",
	"csi/_base/Deferred",
	"csi/_base/storage",
	"csi/_base/underscore",
	"csi/config/LocaleResolver",
	"csi/config/LocaleResources",
	"csi/reflection",
	"csi/xml"
], function(lang, win, csi, Deferred, storage, _, LocaleResolver, LocaleResources, reflection, xml) {

	/**
	 * @typedef {Object} ResultCounts
	 * Object holding counts that can be used in result messages
	 * @property {number} displayCount - number of items displayed
	 * @property {number} resultCount - total number of items
	 * @property {number} groupDisplayCount - number of groups displayed
	 * @property {number} groupCount - total number of groups
	 */

	// String: string to display for nodes with no name
	var _emptyName;

	/** @property {LocaleResources} _resources - resources for current locale */
	var _resources;

	/** @property {LocaleResolver} _resolver - object used to resolve localization of resources */
	var _resolver;


	function displayNameOrType(node) {
		return node.$Name || _resolver?.getTypeName(node.$Type);
	}

	function storeLocale(/*String*/loc) {
		// summary: update locale in locale storage so login page shows correctly
		storage.set("Locale", loc);
	}

	return {
		// constants
		ENGLISH: "en",
		FORMAT: "Format_",
		VALIDATE_VALUE: "ValidateValue",

		// Type Resources
		TYPE_NAME: "Name",
		TYPE_PLURAL: "Plural",
		TYPE_TOOLTIP: "Tooltip",

		/** @property {Deferred} deferred - resolved once locale has been initialized */
		deferred: new Deferred(),

		// Object: map of node type to function for resolving node's display name
		displayNameResolvers: {},

		// LocaleResources: resources in which to load new resources from addResources, addTypes, addViews calls
		loaderLocaleResources: null,

		// LocaleResolver: object used to resolve localization of resources
		localeResolver: null,

		// String: name of product
		// class display format
		//	"Class":{
		//		a1:"Attribute Id",
		//		a2:"Attribute Id",
		//		s: "separator string"
		//	}
		_displayFormats: {},

		// Object: string-string mappings used for renaming global instances (such as phases)
		_indexResources: {
			"Sample": "PhaseSample",
			"Production": "PhaseProduction"
		},

		/**
		 * Capitalize the localization text
		 * @param {string} idRes
		 * @param {string[]} [params]
		 * @return {string}
		 */
		capitalizeResource: function(idRes, params) {
			return this.resource(idRes, params).toLocaleUpperCase(this.getLocale(true));
		},

		/**
		 * Capitalize the pure text based on the current locale
		 * @param {string} text
		 * @return {string}
		 */
		capitalizeText: function(text) {
			return _resolver?.capitalizeText(text);
		},

		getDisplayNameResolver: function(/*String*/type) {
			// summary: return function used to resolve name for node
			var f = this.displayNameResolvers[type];
			if (f == null) {
				// discover if type's name attribute has format of Type - if so, then localize by type
				var rc = reflection.getClass(type);
				var rca = rc ? rc.getAttribute(xml.ATTR_NAME, true) : null;
				if (rca && (rca.m_format == "Type")) {
					f = this.displayNameResolvers[type] = lang.hitch(this, this.getNameTypeName);
				} else {
					this.displayNameResolvers[type] = false;
				}
			}

			return f;
		},

		/**
		 * Return lowercase version of the given resource
		 * @param {string} id
		 * @param {string[]} [params]
		 * @return {string}
		 */
		lowercaseResource(id, params) {
			return this.resource(id, params).toLocaleLowerCase(this.getLocale(true));
		},

		registerDisplayNameResolver: function(/*String*/type, /*function(node, cache, loc)*/f) {
			// summary: register function to resolve name for node
			this.displayNameResolvers[type] = f;
		},

		unregisterDisplayNameResolver: function(/*String*/type) {
			// summary: unregister function to resolve name for node
			delete this.displayNameResolvers[type];
		},

		/**
		 * Set resources for locale, indicating that the locale is now initialized;
		 * this function should only ever be called once, and is only exposed outside for testing purposes
		 * @param {LocaleResources} resources
		 */
		setResources: function(resources) {
			if (_resources) {
				throw new Error("locale#setResources can only be called once!");
			}

			_resources = resources;
			_resolver = this.localeResolver = new LocaleResolver(resources);
			_emptyName = resources.resource("NAME_EMPTY");

			// Add resource that represent "CTRL+" for Windows and "⌘" for Mac. Localizable.
			// Usage example: resource "ImagePasteHere"
			resources.addResources({"CMD_KEY": (dojo.isMac ? this.resource("CmdKeyMac") : this.resource("CmdKeyWin"))});

			// add product resources
			var config = win.global["csiConfig"];
			if (config) {
				resources.addResources({
					"__ProductBranch__": config.Branch,
					"__ProductName__": config.Product,
					"__ProductVersion__": config.Version
				});
			}

			// notify others that locale is initialized
			this.deferred.resolve();
		},

		getAttributeType: function(/*String*/t) {
			// summary: return localized name for attribute value type
			return _resolver?.getAttributeType(t);
		},

		getDisplayFormat: function(/*String*/type) {
			// summary: return display format for type
			return this._displayFormats[type];
		},

		/**
		 * Return attribute list for request based on attribute format of given type;
		 * if type is abstract, then add attributes from each concrete type
		 * @param {string} nodeType 
		 * @param {boolean} [ignoreLocalizedName] - ignore localized name
		 * @return {string[]}
		 */
		getDisplayFormatAttributeList(nodeType, ignoreLocalizedName) {

			const getAttrList = (type, bForce) => {
				const df = this.getDisplayFormat(type);
				const attrs = [];

				if (df) {
					attrs.push(df.a1, df.a2);
				} else if (bForce) {
					attrs.push(xml.ATTR_NAME);
				} else {
					return [];
				}

				const rc = reflection.getClass(type);
				const bLocalized = !ignoreLocalizedName && rc && rc.isLocalizedItem;
				if (bLocalized) {
					attrs.push(xml.ATTR_LOCALIZED_NAME);
				}

				return [`${type}:${attrs.join(";")}`];
			};

			const rc = reflection.getClass(nodeType);
			if (rc?.abstract) {
				const concreteClasses = rc.getConcreteClasses();
				const bDisplayFormat = concreteClasses && concreteClasses.some((rcConcrete) => this.getDisplayFormat(rcConcrete.getType()));

				if (bDisplayFormat) {
					// gather display formats of all concrete classes; if at least one concrete class has a display format,
					// then need to return attribute list for all
					return concreteClasses.reduce((memo, rcConcrete) => [...memo, getAttrList(rcConcrete.getType(), true)], []);
				} else {
					// display format does not make sense for abstract classes
					return [];
				}
			}

			// return attribute list for single type
			return getAttrList(nodeType);
		},

		getEmptyName: function() {
			// summary: return localized string that indicates and empty node name
			return _emptyName;
		},

		/**
		 * Return localized name for enumValue
		 * @param {string} enumValue
		 * @param {boolean} [capitalize] - if true, return capitalized text
		 * @return {string}
		 */
		getEnumName: function(enumValue, capitalize) {
			return _resolver?.getEnumName(enumValue, capitalize);
		},

		/**
		 * Return localized message for given error code and params
		 * @param {Error} error 
		 * @return {string}
		 */
		getErrorMessage(error) {
			return _resolver?.getErrorMessage(error);
		},

		/**
		 * Return localized message for given error code and params
		 * @param {string} errorCode
		 * @param {Array<string | number>} [params]
		 * @return {string}
		 */
		getErrorMessageForCode(errorCode, params) {
			return _resolver?.getErrorMessageForCode(errorCode, params);
		},

		getFormat: function(/*String*/fmt) {
			// summary: return localized name for attribute format
			return this.resourceIfExists(this.FORMAT + fmt) || fmt;
		},

		getIndexName: function(/*String*/s) {
			// summary: get localized name for index
			var idRes = this._indexResources[s];
			return idRes ? _resources.resource(idRes) : s;
		},

		getLocale: function(/*Boolean*/rfc3066Format) {
			return _resolver?.getLocale(rfc3066Format);
		},

		/**
		 * Get node name or return type if there is none or "".
		 * @param {Node} node - node to get name for
		 * @param {Cache} cache - cacheView / m_cache
		 */
		getNodeNameOrType(node, cache) {
			return this.getNodeStoredName(node, cache) || this.getNodeTypeName(node);
		},

		getNameTypeName: function(node) {
			// summary: return type name for node name
			return node ? _resolver?.getTypeName(node.$Name) : null;
		},

		getNodeConfiguredName: function(/*Node*/node, /*Cache*/cache, /*String?*/loc) {
			// summary: return display name for node taking into account all name configurations (display name resolver and BO display format)
			if (!node) {
				return "";
			}

			var s = this.getNodeReferenceName(node, cache, loc);
			var df = this.getDisplayFormat(node.$Type);
			if (df) {
				var bFirst = (df.a1 == xml.ATTR_NAME);
				var id = bFirst ? df.a2 : df.a1;
				var s2 = node[id];

				// for display format, only handle formatting for Node Type and enum attributes
				if (id == xml.ATTR_TYPE) {
					s2 = this.type(s2);
				} else {
					var ra = reflection.getAttribute(node, id);
					if (ra && (ra.getType() == xml.ATTR_ENUM)) {
						s2 = this.getEnumName(s2);
					}
				}

				if (s == _emptyName) {
					s = "";
				}

				if (s || s2) {
					if (bFirst) {
						s += (df.s || "") + s2;
					} else {
						s = s2 + (df.s || "") + s;
					}
				}
			}

			return s || _emptyName;
		},

		getNodeName: function(/*Node*/node, /*Cache?*/cache, /*String?*/loc) {
			// summary: return display name for node
			// FIX: need to determine when this should be used as opposed to getNodeConfiguredName and getNodeReferenceName
			if (!node) {
				return null;
			}

			var f = this.getDisplayNameResolver(node.$Type);
			var name = f ? f(node, cache, loc) : null;

			if (name == null) {
				name = this.getNodeStoredName(node, cache, loc);
			}

			return name || _emptyName;
		},

		getNodePropertyName: function(/*Node*/node) {
			// summary: return property name for node (used to render Node Name attribute)
			return node ? node.$Name : "";
		},

		getNodeReferenceName: function(/*Node*/node, /*Cache*/cache, /*String?*/loc) {
			// summary: return resolved name for node, excluding display format (used to render name for Ref attribute)
			return this.getNodeName(node, cache, loc) || "";
		},

		getNodeStoredName: function(/*Node*/node, /*Cache*/cache, /*String?*/loc) {
			// summary: return name for node based on what is stored on server, not including display format or display name resolver (used when filtering Ref attributes)
			if (!node) {
				return null;
			}

			var name;

			// make sure that LocalizedName has not been disabled before using it
			if (node.LocalizedName && reflection.getNodeClass(node).isLocalizedItem) {
				name = node.LocalizedName[loc || this.getLocale()];
			}

			return name || node.$Name;
		},

		getNodeTypeName: function(node) {
			// summary: return type name for node
			return node ? _resolver?.getTypeName(node.$Type) : null;
		},

		getResolver: function(/*Object?*/params) {
			// summary: create resolver for current locale
			return new LocaleResolver(_resources, params);
		},

		/**
		 * Return localized string representing a number of results
		 * @param {string} idRes - resource id to use for localization
		 * @param {ResultCounts} counts - counts needed for resource
		 * @param {boolean} pluralResults - indicates if "results" or "result" should be used
		 * @param {boolean} pluralGroups - indicates if "groups" or "group" should be used
		 * @return {string}
		 */
		getResultCountString: function(idRes, counts, pluralResults, pluralGroups) {
			return this.resource(idRes, [
				counts.resultCount,
				counts.displayCount,
				this.resource(pluralResults ? "MultipleResults" : "OneResult"),
				counts.groupCount,
				counts.groupDisplayCount,
				this.resource(pluralGroups ? "MultipleGroups" : "OneGroup")
			]);
		},

		/**
		 * Return title to display for revision - if not current then use revision name; otherwise add phase name if present
		 * @param {C8Node} masterNode
		 * @param {C8Node} revisionNode
		 * @param {Cache} cache
		 * @return {string}
		 */
		getRevisionNodeName(masterNode, revisionNode, cache) {
			if (!revisionNode) {
				return "";
			}

			const currentRevisionUrl = masterNode.CurrentRevision;
			const phaseMap = masterNode.PhaseCurrentRevisions;
			const phaseUrl = revisionNode[reflection.ATTR_PHASE];
			const revisionUrl = revisionNode.$URL;
			const isCurrent = (revisionUrl === currentRevisionUrl) || (phaseMap && Object.values(phaseMap).includes(revisionUrl));
			const revisionTitleNode = isCurrent ? reflection.getNode(phaseUrl) : revisionNode;
			return this.getNodeName(revisionTitleNode, cache) || "";
		},

		getValidationError: function(/*String*/keyOrType, /*String?*/idAttr) {
			// summary: return validation error for attribute - return default if none exists; first argument could
			// be key returned from getValidationKey or it could be the node type if the attribute id is passed
			var s;

			if (idAttr) {
				s = _resolver?.getTypeAttributeResource(keyOrType, idAttr, _resolver?.statics.ATTR_VALIDATION);
			} else {
				s = _resolver?._stringSubstitute(_resolver?.getParameterResource(keyOrType));
			}

			return s || _resolver?.getResource(this.VALIDATE_VALUE);
		},

		getValidationKey: function(/*String*/type, /*String*/idAttr) {
			// summary: return validation key for type and attribute
			return _resolver?.getTypeAttributeKey(type, idAttr, _resolver.statics.ATTR_VALIDATION);
		},

		isEnumResource: function(/*String*/idRes) {
			// summary: assumption is that resource is enum if it has a colon and does not begin with RegExp
			return idRes.indexOf(":") >= 0;
		},

		parameterResource: function(/*String*/key, /*String[]*/params) {
			// summary: return the message associated with given key (from resources or types) substituting all parameters
			return _resolver?.resolveResource(key, params);
		},

		resource: function(/*String*/idRes, /*String[]*/params) {
			// summary: convert resource to displayable string
			return _resolver?.getResource(idRes, params) ?? idRes;
		},

		resourceIfExists: function(/*String*/idRes, /*String[]*/params) {
			// summary: convert resource to displayable string, but only if it exists
			return _resolver?.getResourceIfExists(idRes, params);
		},

		tooltip: function(/*String*/idRes, /*String[]*/params) {
			// summary: get tooltip for resource
			return _resolver?.getResourceTooltip(idRes, params);
		},

		type: function(/*String*/type) {
			// summary: convert node type to displayable string - types cannot be parameterized
			return _resolver?.getTypeName(type);
		},

		typeAction: function(/*String*/type, /*String*/idAction, /*String[]?*/params) {
			// summary: convert attribute id to displayable string
			return _resolver?.getTypeAction(type, idAction, params);
		},

		typeId: function(/*String*/type, /*String*/idAttr) {
			// summary: convert attribute id to displayable string
			return _resolver?.getTypeAttribute(type, idAttr);
		},

		typeIdAndPath: function(/*String*/type, /*String*/idAttr, /*String?*/pathName) {
			// summary: convert attribute id and path name to displayable string
			return _resolver?.getTypeAttributeAlongPath(type, idAttr, pathName);
		},

		typePath: function(/*String*/type, /*String*/path) {
			// summary: convert path to displayable string
			return _resolver?.getTypePath(type, path);
		},

		typePaths: function(/*String*/type) {
			// summary: convert path to displayable string
			var obj = _resources.typeResource(type, _resolver.statics.TYPE_PATH);
			var a = _.filter(obj, function(path, key) {return (key.charAt(key.length - 1) == "/");});
			return csi.sort(a, function(path) {return -path.length;});
		},

		typePlural: function(/*String*/type) {
			// summary: convert node type to plural string
			return _resolver?.getTypePlural(type);
		},

		typeView: function(/*String*/type, /*String*/idView) {
			// summary: return localized string for view
			return _resolver?.getTypeView(type, idView);
		},

		stringSubstitute: function(/*String*/str, /*String[]*/params) {
			// summary: replace string parameters with those given or those of referenced resources
			return _resolver?._stringSubstitute(str, params);
		},

		// functions to populate resources

		addDisplayFormats: function(/*Object*/obj) {
			// summary: add display formats - populated from Site_Merged files
			lang.mixin(this._displayFormats, obj);
		},

		addResources: function(/*Object*/obj) {
			// summary: forward resource loading to active LocaleResources
			var resources = this.loaderLocaleResources || _resources;
			resources.addResources(obj);
		},

		addTypes: function(/*Object*/obj) {
			// summary: forward resource loading to active LocaleResources
			var resources = this.loaderLocaleResources || _resources;
			resources.addTypes(obj);
		},

		addViews: function(/*Object*/obj) {
			// summary: forward resource loading to active LocaleResources
			var resources = this.loaderLocaleResources || _resources;
			resources.addViews(obj);
		},

		// exports
		displayNameOrType,
		storeLocale,
	};
});